/**
 * WebSocket Message Handlers
 * Extracted from WebSocketManager.ts handleMessage method
 * Each handler processes a specific message type
 */

import { log } from "@/shared/utils/logger";
import type { SyncState, WebSocketState } from "@/shared/apiTypes";

/** Shape of an incoming WebSocket message (loosely typed to match server payloads). */
export interface WebSocketMsg {
  type: string;
  projectId?: string;
  operationId?: string;
  sessionId?: string;
  operation?: Record<string, unknown>;
  result?: Record<string, unknown>;
  error?: string;
  message?: string;
  version?: number;
  serverVersion?: number;
  changeType?: string;
  source?: string;
  [key: string]: unknown;
}

export const isWebSocketMsg = (value: unknown): value is WebSocketMsg =>
  value !== null &&
  typeof value === 'object' &&
  typeof Reflect.get(value, 'type') === 'string' &&
  (Reflect.get(value, 'projectId') === undefined || typeof Reflect.get(value, 'projectId') === 'string') &&
  (Reflect.get(value, 'sessionId') === undefined || typeof Reflect.get(value, 'sessionId') === 'string') &&
  (Reflect.get(value, 'operationId') === undefined || typeof Reflect.get(value, 'operationId') === 'string') &&
  (Reflect.get(value, 'operation') === undefined || isRecord(Reflect.get(value, 'operation'))) &&
  (Reflect.get(value, 'result') === undefined || isRecord(Reflect.get(value, 'result'))) &&
  (Reflect.get(value, 'version') === undefined || typeof Reflect.get(value, 'version') === 'number') &&
  (Reflect.get(value, 'serverVersion') === undefined || typeof Reflect.get(value, 'serverVersion') === 'number');

const isRecord = (value: unknown): value is Record<string, unknown> =>
  value !== null && typeof value === 'object' && !Array.isArray(value);

export interface MessageHandlerContext {
  subscribedProjects: Set<string>;
  currentUserId: string | null;
  currentSessionId: string | null;
  reconnectAttempts: number;
  maxRetries: number;
  startHeartbeat: () => void;
  updateSyncState: (updates: Partial<SyncState>) => void;
  setState: (state: WebSocketState) => void;
  clearReconnectTimer: () => void;
}

export interface MessageHandler {
  (message: WebSocketMsg, context: MessageHandlerContext): void;
}

// Helper to safely access the window-attached singleton
function getWindowSingleton(): { notifyOperationConfirmed?: (id: string, msg: WebSocketMsg) => void; notifyOperationResult?: (id: string, msg: WebSocketMsg) => void } | null {
  return ((window as unknown as Record<string, unknown>).webSocketSingleton as
    { notifyOperationConfirmed?: (id: string, msg: WebSocketMsg) => void; notifyOperationResult?: (id: string, msg: WebSocketMsg) => void }) ?? null;
}

// Handler for "authenticated" messages
export const handleAuthenticated: MessageHandler = (message, context) => {
  // Ensure we're subscribed to the authenticated project
  if (message.projectId && message.projectId !== "default") {
    context.subscribedProjects.add(message.projectId);
    log.info(
      "WebSocket",
      `✅ Confirmed subscription to project ${message.projectId} after authentication`
    );
    log.debug(
      "WebSocket",
      `📋 Total subscriptions: ${Array.from(context.subscribedProjects).join(", ")}`
    );
  }
  // Start heartbeat after authentication
  context.startHeartbeat();
};

// Handler for "pong" messages (heartbeat response)
export const handlePong: MessageHandler = () => {
  // Heartbeat response received - no action needed
};

// Handler for "session-created" messages
export const handleSessionCreated: MessageHandler = () => {
  // Session created - no action needed
};

// Handler for "operation" messages
export const handleOperation: MessageHandler = (message, context) => {
  // Update server version if provided
  if (message.version) {
    context.updateSyncState({ serverVersion: message.version });
  }
};

// Handler for "operation-confirmed" messages
export const handleOperationConfirmed: MessageHandler = (message) => {
  try {
    const op = (message.operation as Record<string, unknown>)?.operation || message.operation;
    const opId = message.operationId || (op as Record<string, unknown>)?.id;
    if (opId && getWindowSingleton()) {
      try {
        getWindowSingleton()?.notifyOperationConfirmed?.(opId as string, message);
      } catch { /* notification is best-effort */ }
    }
  } catch { /* outer guard */ }
};

// Handler for "operation-result" messages
export const handleOperationResult: MessageHandler = (message) => {
  // Process successful operations; notify singleton waiters
  try {
    const op = (message.operation as Record<string, unknown>)?.operation || message.operation;
    const opId = message.operationId || (op as Record<string, unknown>)?.id;
    if (opId && getWindowSingleton()) {
      try {
        getWindowSingleton()?.notifyOperationResult?.(opId as string, message);
      } catch { /* notification is best-effort */ }
    }
  } catch { /* outer guard */ }
  // Parent handler already invoked via onMessage
};

// Handler for "operation-error" messages
export const handleOperationError: MessageHandler = (message) => {
  log.error(
    "WebSocket",
    "Operation failed:",
    message.error || "Unknown error"
  );

  try {
    // If server reports node not found, force a server reload for that project
    const txt = String(message.error || "");
    if (message.code === 'node_not_found' || txt.includes("not found in project")) {
      // Backend guarantees camelCase in WebSocket messages (converted via convert_websocket_message)
      const pid = message.projectId || (message.operation as Record<string, unknown>)?.projectId;
      const multiCtx = (window as unknown as Record<string, unknown>).__multiProjectContext as
        | { reloadProject?: (id: string) => void }
        | undefined;
      if (pid && multiCtx?.reloadProject) {
        multiCtx.reloadProject(pid as string);
      }
    }
  } catch { /* best-effort recovery */ }

  try {
    const op = (message.operation as Record<string, unknown>)?.operation || message.operation;
    const opId = message.operationId || (op as Record<string, unknown>)?.id;
    if (opId && getWindowSingleton()) {
      try {
        getWindowSingleton()?.notifyOperationResult?.(opId as string, message);
      } catch { /* notification is best-effort */ }
    }
  } catch { /* outer guard */ }
};

// Handler for "import-complete" messages
export const handleImportComplete: MessageHandler = () => {
  // Handle import broadcast - parent handler processes via onMessage
};

// Handler for "sync-status" messages
export const handleSyncStatus: MessageHandler = (message, context) => {
  // Update sync state from server
  if (message.serverVersion) {
    context.updateSyncState({ serverVersion: message.serverVersion });
  }
};

// Handler for "external-change" messages
export const handleExternalChange: MessageHandler = (message) => {
  // Handle external database changes (from MongoDB Change Streams)
  log.info("WebSocket", "External database change detected:", {
    changeType: message.changeType,
    projectId: message.projectId, // Backend converts to camelCase via convert_websocket_message
    source: message.source,
  });

  // Notify about external change - the parent component should reload data
  if (message.source === "mongodb-direct") {
    log.warn(
      "WebSocket",
      "⚠️ Database was modified externally. Refreshing data..."
    );
  }
};

// Handler for "error" messages
export const handleError: MessageHandler = (message, context) => {
  log.error("WebSocket", "Server error received:", message);
  log.error("WebSocket", "Error details:", {
    error: message.error,
    message: message.message,
    fullMessage: message,
  });

  // Handle authentication errors specifically
  if (
    message.message === "Invalid authentication data" ||
    message.error === "Invalid authentication data"
  ) {
    log.warn(
      "WebSocket",
      "Authentication failed - stopping reconnection attempts"
    );
    log.warn("WebSocket", "Current credentials:", {
      sessionId: context.currentSessionId,
      userId: context.currentUserId,
    });
    // Stop trying to reconnect if authentication is invalid
    // Set to max retries and clear credentials
    // (These will be handled by the caller)
    context.clearReconnectTimer();
    context.setState("disconnected");
  }
};

// Handler for "connection-established" messages
export const handleConnectionEstablished: MessageHandler = () => {
  log.info("WebSocket", "Connection established with server");
};

// Handler for unknown message types
export const handleUnknown: MessageHandler = (message) => {
  // Only log unknown messages in development
  if (process.env.NODE_ENV === "development") {
    log.debug("WebSocket", "Unknown message type:", message.type);
  }
};

// Message handler registry
export const messageHandlers: Record<string, MessageHandler> = {
  authenticated: handleAuthenticated,
  pong: handlePong,
  "session-created": handleSessionCreated,
  operation: handleOperation,
  "operation-confirmed": handleOperationConfirmed,
  "operation-result": handleOperationResult,
  "operation-error": handleOperationError,
  "import-complete": handleImportComplete,
  "sync-status": handleSyncStatus,
  "external-change": handleExternalChange,
  error: handleError,
  "connection-established": handleConnectionEstablished,
};

// Main message dispatcher
export function dispatchMessage(
  message: WebSocketMsg,
  context: MessageHandlerContext
): void {
  const handler = messageHandlers[message.type] || handleUnknown;
  handler(message, context);
}
